import type { QueryList } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewChild,
  ViewChildren,
  output,
  input,
} from '@angular/core';

import { trackByProp } from '@core/common-legacy';

import { DropdownItemComponent } from '../../../common/dropdown/dropdown-item/dropdown-item.component';
import { DropdownComponent, DropdownWidth } from '../../../common/dropdown/dropdown.component';
import { KeyCode } from '../../../core/keycode';
import { ScrollableDirective } from '../../../core/overlay/scroll/scrollable.directive';

export interface InputSelectDropdownOption {
  label: string;
  value: string | number;
}

@Component({
  selector: 'rt-input-with-select-dropdown',
  templateUrl: 'input-with-select-dropdown.component.html',
  styleUrls: ['./input-with-select-dropdown.component.less'],
  host: {
    '(window:keydown)': 'onKeyDown($event)',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DropdownComponent, ScrollableDirective, DropdownItemComponent],
})
export class InputWithSelectDropdownComponent {
  public readonly currentOption = input<InputSelectDropdownOption>(undefined!);
  public readonly options = input<InputSelectDropdownOption[]>([]);
  public readonly maxHeight = input<number>(300);

  public valueChanges = output<InputSelectDropdownOption>();

  @ViewChild('dropdownContainer', { read: ElementRef, static: true })
  public timeDropdownContainerRef: ElementRef;
  @ViewChildren('itemsQuery', { read: ElementRef })
  public itemsQueryList: QueryList<ElementRef>;

  public trackByOption = trackByProp<InputSelectDropdownOption>({ prop: 'label' });

  public DropdownWidth = DropdownWidth;

  private selectionIndex: number;
  private currentItem: HTMLDivElement;

  public ngOnInit(): void {
    this.selectionIndex = this.getSelectionIndex();
  }

  public ngAfterViewInit(): void {
    this.setScrollOffset();
  }

  public onSelect(option: InputSelectDropdownOption): void {
    this.valueChanges.emit(option);
  }

  public onKeyDown($event: KeyboardEvent): void {
    if ($event.keyCode === KeyCode.DownArrow) {
      if (this.selectionIndex < this.options().length - 1) {
        this.selectionIndex += 1;
      }
    }

    if ($event.keyCode === KeyCode.UpArrow) {
      if (this.selectionIndex > 0) {
        this.selectionIndex -= 1;
      }
    }
    if ($event.keyCode === KeyCode.Enter) {
      this.currentItem.click();
    }

    if ([KeyCode.Enter, KeyCode.DownArrow, KeyCode.UpArrow].includes($event.keyCode)) {
      this.setScrollOffset();
    }
  }

  private setScrollOffset(): void {
    const timeContainer = this.timeDropdownContainerRef.nativeElement;
    const timeItemRef = this.itemsQueryList.find((item, index) => index === this.selectionIndex);

    if (!timeContainer || !timeItemRef) {
      return;
    }

    const timeItem = timeItemRef.nativeElement;
    this.currentItem = timeItem;

    const itemRelativeTop = timeItem.offsetTop - timeContainer.offsetTop;
    timeContainer.scrollTop = itemRelativeTop;
  }

  private getSelectionIndex(): number {
    if (!this.currentOption()) return 0;

    return this.options().findIndex(option => option.value === this.currentOption().value);
  }
}
